import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  MessageCircle, 
  Download, 
  Edit, 
  Trash2, 
  FileText, 
  AlertCircle,
  Zap,
  DollarSign
} from 'lucide-react';
import { Contract } from '../../../types/contract';
import { formatCurrency } from '../../../utils/currencyUtils';
import { ComponentHeader } from './ComponentHeader';

interface QuickActionsSidebarProps {
  contract: Contract;
  statusConfig: {
    label: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: React.ComponentType<{ className?: string }>;
  };
  priorityInfo: {
    color: string;
    label: string;
  };
  onEdit: () => void;
  onDelete: () => void;
  showContent?: boolean;
  onMountBudget: () => void; // Nova prop
}

export const QuickActionsSidebar: React.FC<QuickActionsSidebarProps> = ({
  contract,
  statusConfig,
  priorityInfo,
  onEdit,
  onDelete,
  showContent,
  onMountBudget
}) => {
  const StatusIcon = statusConfig.icon;
  const [displayValue, setDisplayValue] = useState<string | number>(0);
  const [isCounting, setIsCounting] = useState(false);

  useEffect(() => {
    if (showContent && contract.valor) {
      const numValue = parseFloat(contract.valor.toString().replace(',', '.'));

      if (!isNaN(numValue)) {
        setIsCounting(true);
        const targetValue = numValue;
        const duration = 500;
        const steps = 60;
        const increment = targetValue / steps;
        const stepDuration = duration / steps;
        
        let currentValue = 0;
        const timer = setInterval(() => {
          currentValue += increment;
          if (currentValue >= targetValue) {
            currentValue = targetValue;
            setIsCounting(false);
            clearInterval(timer);
          }
          setDisplayValue(currentValue);
        }, stepDuration);
        
        return () => clearInterval(timer);
      } else {
        setDisplayValue(contract.valor);
        setIsCounting(false);
      }
    }
  }, [showContent, contract.valor]);

  const isBudgetPending = !contract.valor || contract.valor.trim().toUpperCase() === 'N/A';
  
  // Verificar se o contrato tem valor válido para mostrar botões de PDF e WhatsApp
  const hasValidValue = contract.valor && 
    contract.valor.trim() !== '' && 
    contract.valor.trim().toUpperCase() !== 'N/A' &&
    !isNaN(parseFloat(contract.valor.toString().replace(',', '.'))) &&
    parseFloat(contract.valor.toString().replace(',', '.')) > 0;

  const handleSendWhatsApp = () => {
    const contractUrl = `https://playkidseventos.com.br/contrato/${contract.id}/pdf`;
    
    // Formatar data no mesmo formato do contrato
    const formattedDate = contract.dataFesta ? (() => {
      try {
        let dateObj;
        
        // Se a data está em formato brasileiro (DD/MM/YYYY)
        if (contract.dataFesta.includes('/')) {
          const [day, month, year] = contract.dataFesta.split('/');
          dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else if (contract.dataFesta.includes('-')) {
          // Se a data está em formato ISO (YYYY-MM-DD)
          dateObj = new Date(contract.dataFesta + 'T00:00:00');
        } else {
          // Outros formatos
          dateObj = new Date(contract.dataFesta);
        }
        
        if (!isNaN(dateObj.getTime())) {
          const dia = dateObj.getDate();
          const mes = dateObj.toLocaleString('pt-BR', { month: 'long' });
          const ano = dateObj.getFullYear();
          return `${dia} de ${mes} de ${ano}`;
        } else {
          return contract.dataFesta;
        }
      } catch {
        return contract.dataFesta;
      }
    })() : 'N/A';
    
    // Formatar horário
    const formattedTime = contract.horarioInicioFesta || 'A definir';
    const formattedEndTime = contract.fimRecreacao || 'A definir';

    const message = `🎉 *CONTRATO PLAY KIDS RECREAÇÃO INFANTIL* 🎉

📋 *Dados do Contrato:*
• *Contratante:* ${contract.contratante}
• *Data da Festa:* ${formattedDate}
• *Horário:* ${formattedTime} às ${formattedEndTime}
• *Cidade:* ${contract.cidade}
• *Tema:* ${contract.temaFesta || 'A definir'}
• *Aniversariante:* ${contract.nomeAniversariante}
• *Convidados:* ${contract.numeroConvidados} pessoas

📄 *Download do Contrato:*
${contractUrl}

🏠 *Endereço do Evento:*
${contract.endereco}, ${contract.numero}${contract.complemento ? `, ${contract.complemento}` : ''}
${contract.bairro} - ${contract.cidade}/${contract.estado}

📞 *Contato:* +55 33 98407-2711

✨ *Obrigado por escolher a Play Kids!* ✨`;

    // Formatar número do telefone do contratante para WhatsApp
    const phoneNumber = contract.telefone.replace(/\D/g, ''); // Remove todos os caracteres não numéricos
    const whatsappUrl = `https://wa.me/55${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="space-y-2">
      {/* Ações Rápidas */}
      <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
        <ComponentHeader
          title="Ações Rápidas"
          subtitle="Ferramentas"
          icon={Zap}
        />
        <div className="p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onEdit}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-xs font-medium"
            >
              <Edit className="w-3 h-3" />
              Editar
            </button>
            <button
              onClick={onDelete}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-xs font-medium"
            >
              <Trash2 className="w-3 h-3" />
              Excluir
            </button>
          </div>
          
          {/* Botão WhatsApp - Apenas se tiver valor válido */}
          {hasValidValue && (
            <button
              onClick={handleSendWhatsApp}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <MessageCircle className="w-3 h-3" />
              Enviar Contrato via WhatsApp
            </button>
          )}
          
          {!isBudgetPending && (
            <button
              onClick={onMountBudget}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <DollarSign className="w-3 h-3" />
              Acessar Orçamento
            </button>
          )}
        </div>
      </div>

      {/* Resumo */}
      <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
        <ComponentHeader
          title="Resumo"
          subtitle={statusConfig.label}
          icon={FileText}
        />
        <div className="p-3">
          <div className="space-y-3">
            {/* Status, Pagamento e Convidados primeiro */}
            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Status:</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.color} border ${statusConfig.borderColor} shadow-sm`}>
                  <StatusIcon className="w-3 h-3 inline mr-1" />
                  {statusConfig.label}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Pagamento:</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  contract.pago 
                    ? 'bg-emerald-500 text-white border border-emerald-600 shadow-sm' 
                    : 'bg-amber-500 text-white border border-amber-600 shadow-sm'
                }`}>
                  {contract.pago ? 'Pago' : 'Pendente'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Convidados:</span>
                <span className="font-semibold text-slate-800 text-xs">
                  {contract.numeroConvidados || 'N/A'}
                </span>
              </div>
            </div>

            {/* Valor com destaque especial e animação de contador */}
            {isBudgetPending ? (
              <div className="text-center py-3 bg-gradient-to-r from-slate-50 to-slate-50 rounded-lg border border-slate-200">
                <div className="text-xs text-slate-600 font-medium mb-1">VALOR DO CONTRATO</div>
                <button
                  onClick={onMountBudget}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 text-base font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 animate-pulse-glow"
                >
                  <FileText className="w-5 h-5" />
                  Fazer Orçamento
                </button>
              </div>
            ) : (
              <div className="text-center py-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <div className="text-xs text-emerald-600 font-medium mb-1">VALOR DO CONTRATO</div>
                <div className="text-2xl font-bold text-emerald-700">
                  {contract.valor && !isNaN(parseFloat(contract.valor.toString().replace(',', '.'))) ? (
                    <span className="inline-block">
                      {showContent && typeof displayValue === 'number' && isCounting
                        ? `R$ ${displayValue.toFixed(2).replace('.', ',')}`
                        : formatCurrency(contract.valor)}
                      {isCounting && (
                        <span className="ml-1 text-emerald-500 animate-pulse">|</span>
                      )}
                    </span>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Priority Alert */}
      {priorityInfo.label && (priorityInfo.label === 'Esta semana' || priorityInfo.label === 'Amanhã' || priorityInfo.label === 'Hoje') && (
        <div
          className={`${priorityInfo.color} text-white p-3 rounded-lg shadow-lg border border-white/20`}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">{priorityInfo.label}</span>
          </div>
          <p className="text-xs mt-1 opacity-90">
            {priorityInfo.label === 'Esta semana' && 'Evento está próximo, verifique os detalhes!'}
            {priorityInfo.label === 'Amanhã' && 'Evento acontece amanhã! Confirme tudo está pronto.'}
            {priorityInfo.label === 'Hoje' && 'Evento é hoje! Boa sorte!'}
          </p>
        </div>
      )}
    </div>
  );
};